import { useCallback } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { TokenState, TabletopItemType } from '@/components/tabletop/types';
import TabletopItem from '@/components/tabletop/components/TabletopItem';

interface TokenProps {
  token: TokenState;
  onDragStart?: (id: string, type: TabletopItemType, event: React.MouseEvent) => void;
  onSelect?: (id: string, type: TabletopItemType, event: ReactMouseEvent) => void;
  onDoubleClick?: (id: string) => void;
  isSelected?: boolean;
  className?: string;
  viewerRotation?: number;
}

// Default token size
const DEFAULT_TOKEN_SIZE = 60;

export default function Token({
  token,
  onDragStart,
  onSelect,
  onDoubleClick,
  isSelected = false,
  className = '',
  viewerRotation = 0
}: TokenProps) {
  const { id, position, rotation, text, color, size, zIndex } = token;
  const labelRotation = -(rotation + viewerRotation);

  // Handle double click to edit
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDoubleClick) onDoubleClick(id);
  }, [id, onDoubleClick]);

  // Handle select with fallback for when onSelect is undefined
  const handleItemSelect = useCallback((id: string, type: TabletopItemType, event: ReactMouseEvent) => {
    if (onSelect) {
      onSelect(id, type, event);
    }
  }, [onSelect]);

  return (
    <TabletopItem
      id={id}
      type="token"
      position={position}
      rotation={rotation}
      width={size || DEFAULT_TOKEN_SIZE}
      height={size || DEFAULT_TOKEN_SIZE}
      isSelected={isSelected}
      onSelect={handleItemSelect}
      onDragStart={onDragStart}
      className={className}
      zIndex={zIndex}
    >
      <div
        className="relative flex h-full w-full items-center justify-center rounded-full border-2 shadow-[0_14px_28px_rgba(15,23,42,0.2)]"
        style={{
          backgroundColor: color,
          borderColor: adjustColorBrightness(color, -20)
        }}
        onDoubleClick={handleDoubleClick}
      >
        <div className="pointer-events-none absolute inset-[3px] rounded-full border border-white/25" />
        <div className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-b from-white/28 via-transparent to-black/10" />
        <span
          className="select-none font-bold text-white"
          style={{
            fontSize: `${Math.min((size || DEFAULT_TOKEN_SIZE) * 0.4, 24)}px`,
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
            transform: `rotate(${labelRotation}deg)`,
            transformOrigin: 'center'
          }}
        >
          {text}
        </span>
      </div>
    </TabletopItem>
  );
}

// Helper function to adjust color brightness
function adjustColorBrightness(color: string, percent: number): string {
  // Parse hex color
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Adjust brightness
  const adjust = (value: number) => Math.max(0, Math.min(255, value + percent));

  const newR = adjust(r).toString(16).padStart(2, '0');
  const newG = adjust(g).toString(16).padStart(2, '0');
  const newB = adjust(b).toString(16).padStart(2, '0');

  return `#${newR}${newG}${newB}`;
}
